/**
 * The Foghorn's cadence config: shape, defaults, and the pure due-check.
 *
 * Shared between the ops Foghorn dialog (Harbormaster sets cadence, stored in
 * vba_adminconfig under 'ops.foghorn') and the reports-scheduler Function App
 * (hourly timer asks foghornIsDue before sounding). All times are Eastern,
 * the home port, regardless of where the Function App's clock lives.
 */

export type FoghornCadence = 'off' | 'daily' | 'weekly' | 'monthly';

/**
 * The bulletin styles the Harbormaster can sound. Each fits a different phase
 * of the daylight push; SOUNDING_INFO carries the when-to-use guidance shown
 * in the dialog.
 */
export type FoghornSounding = 'first-light' | 'beacon' | 'storm-watch' | 'regatta';

export const SOUNDING_INFO: Record<FoghornSounding, { label: string; emoji: string; blurb: string; guidance: string }> = {
  'first-light': {
    label: 'First Light',
    emoji: '⛵',
    blurb: 'Rally the fleet: harbors charted, quests in flight, and where to start.',
    guidance: 'Best EARLY in the push, while beams are scarce. Shows no percentages, no podium, and no empty award, so it cannot demoralize.',
  },
  beacon: {
    label: 'The Beacon',
    emoji: '📯',
    blurb: 'The full bulletin: progress, leading teams, Bravo Zulu, storm signals, quests.',
    guidance: 'The standard sounding once beams are lighting steadily. A near-0% horizon reads bleak this early; start with First Light instead.',
  },
  'storm-watch': {
    label: 'Storm Watch',
    emoji: '⚠️',
    blurb: 'Only the harbors nearing renewal without daylight, most urgent first.',
    guidance: 'Works at any phase when there is a renewal crunch. Pure call to action; no standings, no scores.',
  },
  regatta: {
    label: 'The Regatta',
    emoji: '🏁',
    blurb: 'Standings-forward: both podiums, Bravo Zulu with runners-up, team races.',
    guidance: 'Best LATER in the voyage, when several teams have beams on the board and weekly wins worth racing over.',
  },
};

export const FOGHORN_SOUNDINGS = Object.keys(SOUNDING_INFO) as FoghornSounding[];

export interface FoghornConfigValue {
  cadence: FoghornCadence;
  /** 0=Sunday … 6=Saturday. Used when cadence='weekly'. */
  dayOfWeek: number;
  /** Hour of day in Eastern time, 0–23. */
  hourLocal: number;
  /** Which bulletin the SCHEDULED sounding sends (manual sends can override). */
  sounding: FoghornSounding;
  /** Eastern date (YYYY-MM-DD) of the last automatic sounding — dedupe stamp. */
  lastSoundedOn?: string;
}

export const FOGHORN_CONFIG_KEY = 'ops.foghorn' as const;
export const FOGHORN_TIME_ZONE = 'America/New_York';

export const DEFAULT_FOGHORN_CONFIG: FoghornConfigValue = {
  cadence: 'off',
  dayOfWeek: 1, // Monday
  hourLocal: 8, // 8am Eastern
  // The safe default: early-voyage bulletin that cannot demoralize.
  sounding: 'first-light',
};

const CADENCES: FoghornCadence[] = ['off', 'daily', 'weekly', 'monthly'];

/** Merge a stored (possibly partial/legacy) value over the defaults, clamped. */
export function normalizeFoghornConfig(raw: unknown): FoghornConfigValue {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<FoghornConfigValue>;
  const cadence = CADENCES.includes(r.cadence as FoghornCadence) ? (r.cadence as FoghornCadence) : DEFAULT_FOGHORN_CONFIG.cadence;
  const dayOfWeek = Number.isInteger(r.dayOfWeek) && (r.dayOfWeek as number) >= 0 && (r.dayOfWeek as number) <= 6
    ? (r.dayOfWeek as number)
    : DEFAULT_FOGHORN_CONFIG.dayOfWeek;
  const hourLocal = Number.isInteger(r.hourLocal) && (r.hourLocal as number) >= 0 && (r.hourLocal as number) <= 23
    ? (r.hourLocal as number)
    : DEFAULT_FOGHORN_CONFIG.hourLocal;
  const sounding = FOGHORN_SOUNDINGS.includes(r.sounding as FoghornSounding)
    ? (r.sounding as FoghornSounding)
    : DEFAULT_FOGHORN_CONFIG.sounding;
  return { cadence, dayOfWeek, hourLocal, sounding, lastSoundedOn: typeof r.lastSoundedOn === 'string' ? r.lastSoundedOn : undefined };
}

export interface FoghornClock {
  /** YYYY-MM-DD in the home-port timezone. */
  dateKey: string;
  hour: number;
  dayOfWeek: number;
  dayOfMonth: number;
}

/** The current wall clock at the home port, from any UTC instant. */
export function foghornClock(now: Date, timeZone: string = FOGHORN_TIME_ZONE): FoghornClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    weekday: 'short',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hourRaw = parseInt(get('hour'), 10);
  return {
    dateKey: `${get('year')}-${get('month')}-${get('day')}`,
    // Some ICU builds render midnight as "24" with hour12:false.
    hour: Number.isFinite(hourRaw) ? hourRaw % 24 : 0,
    dayOfWeek: Math.max(0, dows.indexOf(get('weekday'))),
    dayOfMonth: parseInt(get('day'), 10) || 1,
  };
}

/**
 * Should the hourly timer sound the Foghorn right now? True when the cadence
 * matches the home-port clock AND it hasn't already sounded today (manual
 * "Sound now" clicks don't stamp lastSoundedOn; they never suppress the
 * scheduled sounding).
 */
export function foghornIsDue(cfg: FoghornConfigValue, now: Date): boolean {
  if (cfg.cadence === 'off') return false;
  const clock = foghornClock(now);
  if (clock.hour !== cfg.hourLocal) return false;
  if (cfg.lastSoundedOn === clock.dateKey) return false;
  switch (cfg.cadence) {
    case 'daily': return true;
    case 'weekly': return clock.dayOfWeek === cfg.dayOfWeek;
    case 'monthly': return clock.dayOfMonth === 1;
    default: return false;
  }
}

export const FOGHORN_DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "8:00 AM" style label for an hourLocal value. */
export function foghornHourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00 ${hour < 12 ? 'AM' : 'PM'}`;
}

/** Human summary of a cadence config ("Weekly · Monday · 8:00 AM Eastern"). */
export function foghornCadenceLabel(cfg: FoghornConfigValue): string {
  switch (cfg.cadence) {
    case 'off': return 'Off';
    case 'daily': return `Daily · ${foghornHourLabel(cfg.hourLocal)} Eastern`;
    case 'weekly': return `Weekly · ${FOGHORN_DAY_LABELS[cfg.dayOfWeek] ?? 'Monday'} · ${foghornHourLabel(cfg.hourLocal)} Eastern`;
    case 'monthly': return `Monthly · 1st · ${foghornHourLabel(cfg.hourLocal)} Eastern`;
  }
}
